/**
 * Trunnion AI - Error Boundary
 * Generic React error boundary that catches rendering errors in child components.
 * Accepts a custom fallback UI or renders the default ErrorFallback component.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';

interface ErrorBoundaryProps {
  /** Custom fallback UI to render when an error occurs. */
  fallback?: ReactNode;
  /** Callback invoked when an error is caught. Receives the error and component stack info. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Child components to render when no error is present. */
  children: ReactNode;
  /** Optional key to reset the error boundary when it changes. */
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[Trunnion AI] Error Boundary caught an error:', error);
    console.error('[Trunnion AI] Component stack:', errorInfo.componentStack);

    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset the error state when the resetKey prop changes
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // If a custom fallback is provided, render it directly
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise render the default ErrorFallback
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.resetErrorBoundary}
        />
      );
    }

    return this.props.children;
  }
}
